export interface Ingredient {
  name: string;
  quantity: number;
  unit: string;
}

const FRACTIONS: [number, string][] = [
  [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'], [1 / 2, '½'],
  [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'], [7 / 8, '⅞'],
];

// 0.25 -> "¼", 1.5 -> "1½", 0.333 -> "⅓"; anything that isn't close to a
// common kitchen fraction falls back to at most two decimals. 0 (the import
// agent's "no clean amount" marker) renders as nothing.
export const formatQuantity = (quantity: number): string => {
  if (!quantity || quantity < 0) return '';
  const whole = Math.floor(quantity);
  const remainder = quantity - whole;
  if (remainder < 0.02) return String(whole);
  if (remainder > 0.98) return String(whole + 1);
  const match = FRACTIONS.find(([value]) => Math.abs(remainder - value) < 0.02);
  if (match) return `${whole || ''}${match[1]}`;
  return String(Math.round(quantity * 100) / 100);
};

export const ingredientAmount = (ing: Ingredient): string =>
  [formatQuantity(ing.quantity), ing.unit].filter(Boolean).join(' ');

export const ingredientLabel = (ing: Ingredient): string =>
  [ingredientAmount(ing), ing.name].filter(Boolean).join(' ');

const LEADING_NUMBER = /^\s*(?:step\s*)?\d+\s*[.):-]\s*/i;

// Instructions are stored as one step per line for recipes generated now,
// but older/manual/imported recipes can be a single paragraph -- or inline
// "1. ... 2. ..." numbering. Split whichever shape it is into steps.
export const splitInstructions = (text: string): string[] => {
  const trimmed = (text || '').trim();
  if (!trimmed) return [];

  const clean = (parts: string[]) =>
    parts.map((p) => p.replace(LEADING_NUMBER, '').trim()).filter(Boolean);

  const lines = clean(trimmed.split(/\n+/));
  if (lines.length > 1) return lines;

  const numbered = clean(trimmed.split(/\s+(?=(?:step\s*)?\d+[.)]\s)/i));
  if (numbered.length > 1) return numbered;

  // One unnumbered paragraph: one step per sentence.
  const sentences = trimmed.match(/[^.!?]+(?:[.!?]+|$)/g) || [trimmed];
  return clean(sentences);
};
